import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { Loading, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ClockAlert, Plus, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const istToday = () => new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10);

// HR / Super Admin view of attendance corrections. Super Admin approval applies
// the correction; an HR "approve" only recommends it to Super Admin, and
// anything HR raises itself also waits for Super Admin.
export default function AttendanceCorrectionsAdmin() {
  const qc = useQueryClient();
  const { user, isSuperAdmin } = useAuth();
  const [filter, setFilter] = useState("open");
  const [deciding, setDeciding] = useState(null);   // { req, action }
  const [remarks, setRemarks] = useState("");
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [f, setF] = useState({ employee_id: "", date: istToday(), check_in_time: "", check_out_time: "", reason: "" });

  const { data: rows, isLoading } = useQuery({
    queryKey: ["attCorrections", filter],
    queryFn: async () => (await api.get("/attendance/corrections", { params: filter === "all" ? {} : { status: filter } })).data,
  });
  const { data: employees } = useQuery({ queryKey: ["employeesLite"], queryFn: async () => (await api.get("/employees")).data, enabled: raiseOpen });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["attCorrections"] }); qc.invalidateQueries({ queryKey: ["attCorrectionsCount"] }); };

  const decide = useMutation({
    mutationFn: async () => (await api.post(`/attendance/corrections/${deciding.req.id}/decide`, { action: deciding.action, remarks })).data,
    onSuccess: (d) => {
      const msg = d.status === "Pending_SA" ? "Recommended — sent to Super Admin for approval"
        : d.status === "Approved" ? `Applied — day is now ${d.applied?.status_after}` : "Request rejected";
      toast.success(msg);
      (d.applied?.notes || []).forEach((n) => toast.info(n));
      refresh(); setDeciding(null);
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const raise = useMutation({
    mutationFn: async () => (await api.post("/attendance/corrections", f)).data,
    onSuccess: (d) => {
      toast.success(d.status === "Approved" ? `Correction applied — day is now ${d.applied?.status_after}` : "Correction sent to Super Admin for approval");
      (d.applied?.notes || []).forEach((n) => toast.info(n));
      refresh(); setRaiseOpen(false); setF({ employee_id: "", date: istToday(), check_in_time: "", check_out_time: "", reason: "" });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const canDecide = (r) => {
    if (!["Pending", "Pending_SA"].includes(r.status)) return false;
    if (isSuperAdmin) return true;
    return r.status === "Pending" && r.raised_by_user_id !== user?.id;
  };
  const approveLabel = isSuperAdmin ? "Approve & apply" : "Recommend to Super Admin";

  return (
    <div>
      <div className="flex flex-wrap gap-3 items-end mb-4">
        <div className="w-48">
          <Label>Show</Label>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="open">Open</SelectItem><SelectItem value="Pending_SA">Waiting for Super Admin</SelectItem>
              <SelectItem value="Approved">Approved</SelectItem><SelectItem value="Rejected">Rejected</SelectItem><SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-slate-500 max-w-md">
          {isSuperAdmin ? "Your approval applies the correction immediately." : "Your approval is a recommendation — Super Admin gives the final approval."} Corrected times follow the normal grace / late / half-day rules.
        </p>
        <Button className="ml-auto bg-navy hover:bg-navy-700" onClick={() => setRaiseOpen(true)} data-testid="raise-correction"><Plus className="h-4 w-4 mr-1" /> Correct attendance</Button>
      </div>

      {isLoading ? <Loading /> : !rows?.length ? (
        <EmptyState icon={ClockAlert} title="No attendance corrections" hint="Requests from employees who missed a check-in or check-out appear here." />
      ) : (
        <Card className="border-slate-200 shadow-none overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              {["Employee", "Date", "Recorded", "Requested", "Reason", "Raised by", "Status", ""].map((h) => <TableHead key={h} className="overline text-slate-500">{h}</TableHead>)}
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium text-navy">{r.employee_name}<span className="block text-xs text-slate-400 font-mono-i">{r.employee_code}</span></TableCell>
                  <TableCell className="font-mono-i text-sm text-teal whitespace-nowrap">{r.date}</TableCell>
                  <TableCell className="text-xs text-slate-600">{r.current ? <>In {r.current.check_in || "—"} · Out {r.current.check_out || "—"}<div className="text-slate-400">{r.current.status}</div></> : <span className="text-red-600">No punch (Absent)</span>}</TableCell>
                  <TableCell className="text-xs text-navy font-medium">In {r.requested_check_in || "—"} · Out {r.requested_check_out || "—"}
                    {r.applied && <div className="text-slate-500 font-normal">Result: {r.applied.status_after}</div>}</TableCell>
                  <TableCell className="text-xs text-slate-500 max-w-[220px]">{r.reason}</TableCell>
                  <TableCell className="text-xs text-slate-500">{r.raised_by_role === "EMPLOYEE" ? "Employee" : r.raised_by_email}
                    {r.recommended_by && <div className="text-teal">Recommended by {r.recommended_by}</div>}</TableCell>
                  <TableCell><StatusBadge status={r.status} />{r.decision_remarks && <div className="text-[11px] text-slate-400 max-w-[140px]">{r.decision_remarks}</div>}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {canDecide(r) && <>
                      <Button size="sm" variant="ghost" className="text-emerald-600" title={approveLabel} onClick={() => { setDeciding({ req: r, action: "approve" }); setRemarks(""); }}><CheckCircle2 className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-500" title="Reject" onClick={() => { setDeciding({ req: r, action: "reject" }); setRemarks(""); }}><XCircle className="h-4 w-4" /></Button>
                    </>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={!!deciding} onOpenChange={(o) => !o && setDeciding(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>{deciding?.action === "approve" ? approveLabel : "Reject request"} — {deciding?.req.employee_name}, {deciding?.req.date}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Requested: in {deciding?.req.requested_check_in || "—"}, out {deciding?.req.requested_check_out || "—"}</p>
          <p className="text-xs text-slate-500">{deciding?.req.reason}</p>
          <div><Label>Remarks {deciding?.action === "reject" ? "*" : "(optional)"}</Label><Input className="mt-1" value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder={deciding?.action === "reject" ? "Why it can't be accepted" : "e.g. Confirmed with reporting officer"} /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeciding(null)}>Back</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={decide.isPending || (deciding?.action === "reject" && !remarks.trim())} onClick={() => decide.mutate()}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={raiseOpen} onOpenChange={setRaiseOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Correct an employee's attendance</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">{isSuperAdmin ? "Applied immediately and recorded in the audit trail." : "Goes to Super Admin for approval before it's applied."}</p>
          <div className="space-y-3">
            <div>
              <Label>Employee</Label>
              <Select value={f.employee_id} onValueChange={(v) => setF({ ...f, employee_id: v })}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Choose employee" /></SelectTrigger>
                <SelectContent className="bg-white">{(employees || []).map((e) => <SelectItem key={e.id} value={e.id}>{e.identity?.name} ({e.employee_id})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input type="date" className="mt-1" max={istToday()} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Check-in</Label><Input type="time" className="mt-1" value={f.check_in_time} onChange={(e) => setF({ ...f, check_in_time: e.target.value })} /></div>
              <div><Label>Check-out</Label><Input type="time" className="mt-1" value={f.check_out_time} onChange={(e) => setF({ ...f, check_out_time: e.target.value })} /></div>
            </div>
            <div><Label>Reason / evidence *</Label><Textarea rows={3} className="mt-1" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="e.g. Biometric outage at Vaishali Nagar centre; gate register shows 09:02" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRaiseOpen(false)}>Cancel</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={!f.employee_id || !f.date || !(f.check_in_time || f.check_out_time) || f.reason.trim().length < 10 || raise.isPending} onClick={() => raise.mutate()}>
              {isSuperAdmin ? "Apply correction" : "Send for approval"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
